import { Box, Button, Grid, Modal, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { isAdmin as checkIsAdmin, isManager as checkIsManager } from '../../../utils/share';
import { BILLABLE_OPTION, GRAPH_LIST, INITIAL_FILTER_VALUES } from '../constants';
import { useStyles } from '../Style/filterBoxStyles';
import { FilterAutocomplete } from './ui';

import { PROJECT_STATUS_LIST, TASK_STATUS_LIST, TASK_TYPE_LIST } from '../../../Constants/ProjectConstants';
import { filterClientList, getAllClients } from '../../../services/clientService';
import { getAllUsers, getTeamMembers } from '../../../services/teamService';

const FilterBox = ({ handleSearchClick }) => {
  const classes = useStyles();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [allClientList, setAllClientList] = useState([]);
  const [allProjectList, setAllProjectList] = useState([]);
  const [allEmployeeList, setAllEmployeeList] = useState([]);

  const isAdmin = checkIsAdmin();
  const isManager = checkIsManager();
  const currentUser = useSelector((state) => state?.login?.user);

  const showEmployeeFilter = isAdmin || isManager;

  const openDateRangePicker = () => setShowDatePicker(true);
  const closeDateRangePicker = () => setShowDatePicker(false);

  const formik = useFormik({
    initialValues: INITIAL_FILTER_VALUES,
    onSubmit: handleSearchClick,
  });

  const handleFilterProjectList = () => {
    const selectedClientIds = formik?.values?.clientList?.map((client) => client?.id) || [];
    const selectedProjectStatusList = formik?.values?.projectStatusList || [];

    let filterProjectList = [...allProjectList];

    if (selectedClientIds.length > 0) {
      filterProjectList = filterProjectList.filter((project) => selectedClientIds?.includes(project?.clientId));
    }
    if (selectedProjectStatusList?.length > 0) {
      filterProjectList = filterProjectList.filter((project) => selectedProjectStatusList.includes(project?.status));
    }
    return filterProjectList;
  };

  const handleClientChange = (_event, values) => {
    formik.setFieldValue('clientList', values);
    if (values?.length > 0) {
      const selectedClientIds = values?.map((client) => client?.id);
      const projectList = formik.values?.projectList.filter((project) =>
        selectedClientIds?.includes(project?.clientId)
      );
      formik.setFieldValue('projectList', projectList);
    }
  };

  const handleProjectStatusChange = () => (_event, values) => {
    formik.setFieldValue('projectStatusList', values);
    if (values?.length > 0) {
      const filterProjectList = formik.values?.projectList.filter((project) => values?.includes(project?.status));
      formik.setFieldValue('projectList', filterProjectList);
    }
  };

  const handleTaskStatusChange = () => (_event, values) => {
    formik.setFieldValue('taskStatusList', values);
  };

  const handleTaskTypeChange = (_event, values) => {
    formik.setFieldValue('taskTypeList', values);
  };

  const handleAutocompleteChange = useCallback(
    (fieldName) => (_event, values) => {
      formik.setFieldValue(fieldName, values);
    },
    [formik]
  );

  const handleDateRangeChange = (ranges) => {
    const prev = formik?.values?.dateRange;
    const newRange = { ...prev, ...ranges };
    formik.setFieldValue('dateRange', newRange);
  };

  const handleTaskNoteChange = (event) => {
    formik.setFieldValue('taskNote', event.target.value);
  };

  useEffect(() => {
    const fetchClientList = async () => {
      const allClientsResponse = isAdmin ? await getAllClients() : await filterClientList();
      setAllClientList(
        allClientsResponse?.data?.data?.rows?.map((data) => ({
          id: data?.id,
          name: data?.clientName,
        }))
      );

      // Extract and flatten the projects list
      const projectsList = allClientsResponse?.data?.data?.rows?.map((data) => data?.projects).flat();

      // Remove duplicate projects and sort alphabetically by project name
      const uniqueProjects = Array.from(new Set(projectsList.map((project) => project.id)))
        .map((id) => projectsList.find((project) => project.id === id))
        .sort((a, b) => a.projectName.localeCompare(b.projectName));

      // Set the filtered and sorted project list
      setAllProjectList(
        uniqueProjects.map((data) => ({
          id: data?.id,
          name: data?.projectName,
          clientId: data?.clientId,
          status: data?.status,
        }))
      );
    };

    // const fetchProjectList = async () => {
    //   const allProjectsResponse = await getAllProjects();
    //   setAllProjectList(
    //     allProjectsResponse?.data?.data?.rows?.map((data) => ({
    //       id: data?.id,
    //       name: data?.projectName,
    //       clientId: data?.clientId,
    //       status: data?.status,
    //     }))
    //   );
    // };

    const fetchEmployeeList = async () => {
      if (showEmployeeFilter) {
        const allEmployeesResponse = await getAllUsers();
        const employeeList = allEmployeesResponse?.data?.data?.map((data) => ({
          id: data?.id,
          name: data?.userName,
        }));

        if (isAdmin) {
          setAllEmployeeList(employeeList);
        } else if (isManager) {
          const getTeamMembersResponse = await getTeamMembers();
          const allowedList = getTeamMembersResponse?.data?.data ?? [];
          allowedList.push(currentUser?.id);

          const teamMemberList = employeeList.filter((data) => allowedList.includes(data?.id));
          setAllEmployeeList(teamMemberList);
        }
      }
    };

    const fetchAllList = async () => {
      try {
        setIsLoading(true);
        await Promise.all([fetchClientList(), fetchEmployeeList()]);
      } catch (error) {
        console.log('error', error?.message);
        toast.error(error?.response?.data?.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllList();
  }, [currentUser.id, isAdmin, isManager, showEmployeeFilter]);

  function formatDate(dateString) {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  const selectedDateRangeText = `${formatDate(formik?.values?.dateRange?.selection?.startDate)} - ${formatDate(
    formik?.values?.dateRange?.selection?.endDate
  )}`;

  return (
    <Grid container className={classes.rootContainer}>
      <Grid item sm={10} xs={12} className={classes.rootItem}>
        <Grid container>
          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Date Range: </Typography>
          </Grid>
          <Grid item md={8}>
            <Box>
              <Button onClick={openDateRangePicker} className={classes.dateRangeBtn}>
                {selectedDateRangeText}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Client : </Typography>
          </Grid>
          <Grid item md={4}>
            <FilterAutocomplete
              value={formik?.values?.clientList}
              onChange={handleClientChange}
              options={allClientList}
              loading={isLoading}
            />
          </Grid>

          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Project : </Typography>
          </Grid>
          <Grid item md={4}>
            <FilterAutocomplete
              value={formik?.values?.projectList || []}
              onChange={handleAutocompleteChange('projectList')}
              options={handleFilterProjectList()}
              renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ textTransform: 'capitalize' }}>
                  {option.name}
                </li>
              )}
              getOptionLabel={(option) => option.name || ''}
              loading={isLoading}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Project Status : </Typography>
          </Grid>
          <Grid item md={4}>
            <FilterAutocomplete
              value={formik?.values?.projectStatusList}
              onChange={handleProjectStatusChange()}
              options={PROJECT_STATUS_LIST.sort()}
              getOptionLabel={(option) => option}
              renderOption={(props, option) => (
                <li style={{ textTransform: 'capitalize' }} {...props}>
                  {option}
                </li>
              )}
              title="Status of the project"
            />
          </Grid>

          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Task Type : </Typography>
          </Grid>
          <Grid item md={4}>
            <FilterAutocomplete
              value={formik.values.taskTypeList}
              onChange={handleTaskTypeChange} // Pass the function directly
              options={TASK_TYPE_LIST}
              getOptionLabel={(option) => option}
              renderOption={(props, option) => <li {...props}>{option}</li>}
              title="Type of the task"
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Report Name : </Typography>
          </Grid>
          <Grid item md={4}>
            <FilterAutocomplete
              value={formik?.values?.graphList}
              onChange={handleAutocompleteChange('graphList')}
              options={GRAPH_LIST?.sort()}
              getOptionLabel={(option) => option}
              renderOption={(props, option) => <li {...props}>{option}</li>}
            />
          </Grid>

          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Keyword : </Typography>
          </Grid>
          <Grid item md={4}>
            <TextField
              value={formik?.values?.taskNote}
              className={classes.textField}
              onChange={handleTaskNoteChange}
              placeholder="Keyword Search"
              size="small"
              variant="standard"
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid item md={2} className={classes.labelContainer}>
            <Typography>Task Status : </Typography>
          </Grid>
          <Grid item md={4}>
            <FilterAutocomplete
              value={formik?.values?.taskStatusList}
              onChange={handleTaskStatusChange()}
              options={TASK_STATUS_LIST.sort()}
              getOptionLabel={(option) => option}
              renderOption={(props, option) => (
                <li style={{ textTransform: 'capitalize' }} {...props}>
                  {option}
                </li>
              )}
              title="Status of the project"
            />
          </Grid>
          <Grid item md={2} className={classes.labelContainer}>
            Tasks Category :
          </Grid>
          <Grid item md={4}>
            <FilterAutocomplete
              value={formik.values.billableTypeList}
              onChange={handleAutocompleteChange('billableTypeList')}
              options={BILLABLE_OPTION}
              loading={isLoading}
            />
          </Grid>
        </Grid>

        <Grid container>
          {showEmployeeFilter && (
            <>
              <Grid item md={2} className={classes.labelContainer}>
                <Typography>Employee : </Typography>
              </Grid>
              <Grid item md={4}>
                <FilterAutocomplete
                  value={formik?.values?.employeeList}
                  onChange={handleAutocompleteChange('employeeList')}
                  options={allEmployeeList}
                  loading={isLoading}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box className={classes.submitContainer}>
          <Button variant="contained" onClick={formik.handleSubmit} sx={{ width: '20%' }}>
            Search
          </Button>
        </Box>
      </Grid>
      <Modal open={showDatePicker} onClose={closeDateRangePicker}>
        <Box className={classes.dateRangeModal}>
          <DateRangePicker
            onChange={handleDateRangeChange}
            months={1}
            direction="vertical"
            scroll={{ enabled: true }}
            ranges={[formik?.values?.dateRange?.selection]}
            maxDate={new Date()}
          />
          <Button variant="contained" sx={{ marginTop: '-40px' }} onClick={closeDateRangePicker}>
            Apply
          </Button>
        </Box>
      </Modal>
    </Grid>
  );
};

FilterBox.propTypes = {
  handleSearchClick: PropTypes.func,
};

export default FilterBox;
